/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 25 2024
 **/

import { Updater, useQueryClient } from "@tanstack/react-query";

import { useStompManager } from "@/frameworks/libraries/network";
import { Properties } from "@/frameworks/libraries/properties";

import { useChat } from "@/frameworks/core/API";
import { Topics } from "@/frameworks/core/commons";
import { usePersistActions } from "@/frameworks/core/persistence";

type SpaceChatListMap = Map<string, WebServices.SpaceChat.List.Data[number]>;

/**
 * @description
 * 1. 공간 미참여 상태에서 개방형 오픈월드 채팅 참여 시
 *    - POST /api/space_chat/join 호출
 *    - RoomView API 호출 (view=true)
 * 2. 공간 미참여 상태에서 개방형 오픈월드 채팅 나가기 시
 *    - POST /api/space_chat/leave 호출 불필요
 *    - RoomView API로 대체 (view=false)
 * 3. 개방형 오픈월드 공간 참여 시
 *    - POST /api/space_chat/join 호출 불필요
 *    - 공간 참여 시 채팅 참여는 서버에서 처리
 * 4. 공간 미참여 상태에서 마이룸 채팅 참여 시 space_chat/join|leave API 호출 불필요
 *    (마이룸 주인이 자신의 마이룸 공간 미참여 상태로 채팅 참여하는 경우)
 *    - POST /api/space_chat/join, POST /api/space_chat/leave 호출 불필요 (마이룸 대상)
 * 5. POST /api/space_chat/leave API는 비공개 스페이스 채팅을 나가는 경우에만 호출되어야 함.
 * 6. 공간 참여/미참여 관계없이 채팅방을 보거나 닫을 때
 *    - 채팅방 view (RoomView) 변경 API 호출 필요
 */
export default function useOpenWorldChatActions() {
	const chatAPI = useChat();
	const stompNetwork = useStompManager();
	const queryClient = useQueryClient();
	const { spaces } = usePersistActions();

	const setSpaceChatRoomList = (updater: Updater<SpaceChatListMap | undefined, SpaceChatListMap | undefined>) => {
		queryClient.setQueryData<SpaceChatListMap>(["getSpaceChatList"], updater);
	};

	/**
	 * 채팅방 참가
	 */
	const join = async (spaceID: string, spaceName: string) => {
		const data = (await chatAPI.space.join.execute({ spaceId: spaceID, deviceKey: Properties.browserTabID })).data;

		const roomId = data?.roomId;
		if (!roomId) {
			throw new Error("Failed to join chat room");
		}

		if (!stompNetwork.hasTopics([Topics.Room(roomId)])) {
			stompNetwork.subscribe(Topics.Room(roomId));
		}
		// chatAPI
		const room = (await chatAPI.getRoom.execute({ roomID: roomId })).data;
		let groupId = "";
		if (room?.roomType === "OPEN_WORLD") {
			groupId = (spaces.get_space(spaceID) as V2.OpenWorldSpaceInfo).groupId || "";
		}

		if (room) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				if (!room.spaceId) return prev;
				prev.set(room.spaceId, {
					groupName: room.groupName,
					groupId: groupId,
					spaceName: spaceName,
					spaceType: "OPEN_WORLD",
					myRoom: false,
					rooms: [room],
					spaceId: room.spaceId,
					useSpaceChatNotification: false,
					pinned: false
				});
			});
		}

		return room;
	};

	/**
	 * 채팅방 나가기
	 */
	const leave = async (spaceID: string) => {
		// await chatAPI.space.leave.execute({ roomId: roomID, deviceKey: Properties.browserTabID });
		// stompNetwork.unsubscribe(Topics.Room(roomID));
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			prev.delete(spaceID);
		});
	};

	return {
		join,
		leave
	};
}
